import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useRealtimeEvent } from "../../lib/realtime";
import { Plus, Check, X } from "lucide-react";
import { GlassAvatar, GlassConversation } from "./GlassConversation";

const Row = ({ c, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`cai-contact-${c.id}`}
    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[18px] text-left hover:bg-white/15 transition-colors"
  >
    <GlassAvatar c={c} />
    <span className="flex-1 min-w-0">
      <span className="block text-[13px] font-semibold text-white truncate">{c.name}</span>
      <span className="block text-[11px] text-white/70 truncate">{c.last_message || c.specialty || c.email}</span>
    </span>
    {c.unread > 0 && (
      <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-white text-[#6FA2DE] text-[10px] font-bold grid place-items-center">
        {c.unread > 9 ? "9+" : c.unread}
      </span>
    )}
  </button>
);

export const ChatTab = () => {
  const [contacts, setContacts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [active, setActive] = useState(null);
  const [email, setEmail] = useState("");

  const load = () =>
    Promise.all([api.get("/chat/contacts"), api.get("/chat/contact-requests").catch(() => ({ data: [] }))])
      .then(([c, r]) => { setContacts(c.data || []); setRequests(r.data || []); })
      .catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);
  useRealtimeEvent("chat:new", load);
  useRealtimeEvent("chat:contact_request", load);
  useRealtimeEvent("chat:contact_accepted", load);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      const { data } = await api.post("/chat/contacts", { email: email.trim() });
      toast.success(data.status === "sent" ? "Solicitud enviada" : "Este contacto ya existe o está pendiente");
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pude enviar la solicitud");
    }
  };
  const respond = async (r, ok) => {
    await api.post(`/chat/contact-requests/${r.id}/${ok ? "accept" : "reject"}`).catch(() => {});
    load();
  };

  if (active) return <GlassConversation contact={active} onBack={() => { setActive(null); load(); }} />;

  const online = contacts.filter((c) => c.online);
  const offline = contacts.filter((c) => !c.online);
  const Label = ({ children }) => <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-white/70">{children}</div>;

  return (
    <div className="flex-1 min-h-0 flex flex-col cai-fade-in" data-testid="cai-chat-list">
      <form onSubmit={add} className="mx-5 cai-glass rounded-full h-11 flex items-center pl-4 pr-1.5 gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Agregar doctor por email…"
          data-testid="cai-chat-add-input"
          className="cai-input flex-1 min-w-0 text-[13px] text-white placeholder:text-white/70"
        />
        <button type="submit" data-testid="cai-chat-add-btn" className="w-8 h-8 rounded-full bg-white/90 text-[#6FA2DE] grid place-items-center hover:bg-white transition-colors">
          <Plus className="w-4 h-4" />
        </button>
      </form>
      <div className="flex-1 min-h-0 overflow-y-auto cai-scroll px-2 pb-4">
        {requests.length > 0 && <Label>Solicitudes · {requests.length}</Label>}
        {requests.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-3 py-2" data-testid={`cai-request-${r.id}`}>
            <GlassAvatar c={{ name: r.from_name, photo_url: r.from_photo_url }} />
            <span className="flex-1 min-w-0 text-[13px] text-white truncate">{r.from_name}</span>
            <button type="button" onClick={() => respond(r, true)} className="w-7 h-7 rounded-full bg-white/90 text-[#6FA2DE] grid place-items-center"><Check className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => respond(r, false)} className="w-7 h-7 rounded-full cai-glass text-white grid place-items-center"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <Label>Conectados · {online.length}</Label>
        {online.length === 0 && <div className="px-3 py-1 text-[12px] text-white/70">Nadie conectado ahora</div>}
        {online.map((c) => <Row key={c.id} c={c} onClick={() => setActive(c)} />)}
        <Label>Desconectados · {offline.length}</Label>
        {offline.map((c) => <Row key={c.id} c={c} onClick={() => setActive(c)} />)}
      </div>
    </div>
  );
};
